import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import i18n from 'src/i18n';
import { ErrorToast } from 'src/libs/toast';

import ChapterService from 'src/services/chapter';
import ChapterError from 'src/services/error/models/chapter';

import CommentModalViewModel from 'src/components/CommentModal/viewModel';
import PurchaseAlertViewModel from 'src/components/PurchaseAlert/viewModel';
import ChapterSelectorModalViewModel from 'src/components/ChapterSelectorModal/viewModel';
import CommentCardViewModel from './CommentCard/viewModel';

export default class CommentSectionViewModel {
  @observable courseId;
  @observable isOwned;
  @observable isPurchaseOnly;

  @observable commentList = [];
  @observable chapterList = [
    {
      id: 'all',
      name: i18n.t('course_tab_comments_selectAll')
    }
  ];
  @observable isSelectedChapter = {
    id: 'all',
    name: i18n.t('course_tab_comments_selectAll')
  }
  @observable isSelectedChapterCache = {
    id: 'all',
    name: i18n.t('course_tab_comments_selectAll')
  }

  @observable showMyComments = false;
  @observable anchor = null;
  @observable chapterAnchor = null;
  @observable isMore = false;
  @observable isChapterMore = false;

  @observable commentModalVm = new CommentModalViewModel();
  @observable purchaseAlert = new PurchaseAlertViewModel();
  @observable chapterSelectorModalVm = new ChapterSelectorModalViewModel();

  @observable status = {
    isReady: false,
    isProcessing: false,
    isLoading: false
  }

  constructor() {
    makeObservable(this);
  }

  @action
  didMount = async (data) => {
    try {
      this.courseId = data.id;
      this.isOwned = data.isOwned;
      this.isPurchaseOnly = data.saleType === 'Single';

      await this.getChapters();
      await this.getComments();
      await this.commentModalVm.didMount(this.courseId);
      runInAction(() => {
        this.status.isReady = true;
      });
    } catch (error) {
      console.error(error);
    }
  }

  @action
  getChapters = async () => {
    try {
      const { list, anchor } = await ChapterService.getChapters(this.courseId, this.chapterAnchor);
      runInAction(() => {
        this.chapterList.push(...list);
        this.chapterAnchor = anchor;
        this.isChapterMore = !!anchor;
      });
    } catch (error) {
      ChapterError.getChapters(error);
    }
  }

  @action
  getComments = async () => {
    try {
      if (this.isSelectedChapter.id === 'all') {
        const { list, anchor } = await ChapterService.getChapterComments(this.courseId, this.anchor);
        runInAction(() => {
          const newComment = list.map((el) => new CommentCardViewModel(el));
          this.commentList.push(...newComment);
          this.anchor = anchor;
          this.isMore = !!anchor;
        });
      } else {
        const { list, anchor } = await ChapterService.getSingleChapterComments(this.isSelectedChapter.id, this.anchor);
        runInAction(() => {
          const newComment = list.map((el) => new CommentCardViewModel(el));
          this.commentList.push(...newComment);
          this.anchor = anchor;
          this.isMore = !!anchor;
        });
      }
    } catch (error) {
      ChapterError.getSingleChapterComments(error);
    }
  }

  @action
  getMyComments = async () => {
    try {
      if (this.isSelectedChapter.id === 'all') {
        const { list, anchor } = await ChapterService.getMyComments(this.courseId, this.anchor);
        runInAction(() => {
          const newComment = list.map((el) => new CommentCardViewModel(el));
          this.commentList.push(...newComment);
          this.anchor = anchor;
          this.isMore = !!anchor;
        });
      } else {
        const { list, anchor } = await ChapterService.getMyCommentsByChapter(this.isSelectedChapter.id, this.anchor);
        runInAction(() => {
          const newComment = list.map((el) => new CommentCardViewModel(el));
          this.commentList.push(...newComment);
          this.anchor = anchor;
          this.isMore = !!anchor;
        });
      }
    } catch (error) {
      if (error.response?.status === 404) {
        return;
      }
      ChapterError.getMyCommentsByChapter(error);
    }
  }

  @action
  handleSelect = (el) => {
    this.isSelectedChapterCache = el;
  }

  @action
  onSelectConfirm = async () => {
    try {
      this.isSelectedChapter = this.isSelectedChapterCache;
      this.chapterSelectorModalVm.handlerConfirmCloseAlert();
      this.anchor = null;
      this.commentList = [];
      if (this.showMyComments) {
        await this.getMyComments();
      } else {
        await this.getComments();
      }

    } catch (error) {
      console.error(error);
    }
  }

  @action
  onMyCommentClick = async () => {
    try {
      this.showMyComments = !this.showMyComments;
      this.anchor = null;
      this.commentList = [];
      if (this.showMyComments) {
        await this.getMyComments();
      } else {
        await this.getComments();
      }
    } catch (error) {
      console.error(error);
    }
  }

  @action
  afterConfirmComment = (data) => {
    const targetComment = this.commentList.find((el) => el.id === data.comment.id);
    if (!targetComment) {
      this.commentList.unshift(new CommentCardViewModel(data.comment));
    } else {
      targetComment.comment = data.comment.comment;
      // FIXME: Need better solution.
      setTimeout(() => {
        targetComment.checkForOverflow();
        targetComment.changeOverflow();
      }, 1);
    }
  }

  @action
  onEditOpen = (data) => {
    const modalData = {
      ...data,
      courseId: this.courseId
    };
    this.commentModalVm.onEditOpen(modalData);
  }

  @action
  openModalFunction = (profile) => {
    // if (this.isOwned) {
    //   console.log('trigger redirect');
    //   return;
    // }

    // if (!this.isPurchaseOnly && !!(profile && profile?.group !== 'Member')) {
    //   console.log('trigger redirect');
    //   return;
    // }

    const params = this.isPurchaseOnly ? this.courseId : null;
    this.purchaseAlert.handlerOpenAlert(params);
  }

  // MORE Waypoint function

  @action
  onEnter = async () => {
    try {
      if (!this.status.isReady) {
        return;
      }
      if (this.showMyComments) {
        await this.getMyComments();
      } else {
        await this.getComments();
      }
    } catch (error) {
      console.error(error);
    }
  }

  @computed
  get modalData() {
    return ({
      courseId: this.courseId,
      chapter: this.isSelectedChapter
    });
  }
}
